/**
 * Class ↔ ClassItem 매핑 (Swagger Class API ↔ 기존 UI ClassItem)
 * 백엔드 API 변경: Course → Class (2026-02-02)
 * @see my-app/docs/api/API.md
 */

import type {
	ClassItemInfoDto,
	ClassListItemDto,
	EnrolledClassListItemDto,
	EnrolledClassStatus,
	GetClassDetailResponseDto,
	OpenClassListItemDto,
} from '@/types/api/class';
import type { ClassItem, CurriculumItem } from '@/data/classes';
import {
	coerceClassDateToYyyyMmDd,
	coerceNonNegativeInt,
	coercePositiveInt,
	resolveThumbnailImageUrlFromRecord,
} from '@/utils/classApiNormalize';
import { normalizeLocaleTag } from '@/lib/i18nLocale';
import { formatProductPriceValidityLabel } from '@/lib/productPriceValidityLabel';
import { formatLocaleDateShort } from '@/lib/localeFormat';

/** 클래스 목록 — 세션(커리큘럼 아이템) 수 (API 문서 기준: classItemCount) */
export function classListItemDisplaySessionCount(item: ClassListItemDto & Record<string, unknown>): number {
	return coerceNonNegativeInt(item.classItemCount) ?? 0;
}

/** 클래스 목록 — 참여자 수 (API 문서 기준: classUserMappingCount) */
export function classListItemDisplayParticipantCount(item: ClassListItemDto & Record<string, unknown>): number {
	return coerceNonNegativeInt(item.classUserMappingCount) ?? 0;
}

/**
 * GET /class/organization/list 행 — 수강기간 yyyy-MM-DD (상세·정규화와 동일: class* 우선, 별칭·snake 보조)
 */
export function getClassListItemParticipationYmd(
	item: ClassListItemDto & Record<string, unknown>
): { startYmd: string; endYmd: string } | null {
	const r = item;
	const cs =
		coerceClassDateToYyyyMmDd(r.classStartDate ?? r.class_start_date) ||
		coerceClassDateToYyyyMmDd(r.startDate);
	const ce =
		coerceClassDateToYyyyMmDd(r.classEndDate ?? r.class_end_date) ||
		coerceClassDateToYyyyMmDd(r.endDate);
	if (!cs && !ce) return null;
	const startYmd = cs || ce!;
	// 종료일이 없으면 시작일로 채우지 않고 빈값으로 둔다 (종료일 없음 = 기간 제한 없음)
	const endYmd = ce || '';
	return { startYmd, endYmd };
}

/** 참여기간 표시 문자열 — 종료일이 없으면 `시작일 ~ —` */
export function formatParticipationPeriodLabel(period: { startDate: string; endDate: string }): string {
	return `${period.startDate} ~ ${period.endDate || '—'}`;
}

/**
 * ClassListItemDto → ClassItem (목록/기존 UI 호환)
 * - classId(number) → id(string)
 * - participantCount → currentParticipants
 * - startDate/endDate → participationPeriod
 * - scenarioList → curriculum (간소화)
 */
export function classListItemDtoToClassItem(
	dto: ClassListItemDto,
	options?: { organizationId?: number; organizationName?: string }
): ClassItem {
	const r = dto as ClassListItemDto & Record<string, unknown>;
	const range = getClassListItemParticipationYmd(r);
	const startDate = range?.startYmd ?? '';
	const endDate = range?.endYmd ?? '';

	return {
		id: String(dto.classId),
		type: 'course',
		title: dto.title,
		subtitle: '',
		description: '',
		thumbnail: dto.thumbnailImageUrl ?? undefined,
		curriculum: [],
		classType: dto.isOpenClass ? 'open' : 'my',
		participationPeriod: {
			startDate,
			endDate,
		},
		currentParticipants: classListItemDisplayParticipantCount(dto as ClassListItemDto & Record<string, unknown>),
		createdDate: dto.createdAt?.slice(0, 10),
		isActive: dto.isActive,
		organizationId: options?.organizationId,
		organizationName: options?.organizationName,
	};
}

/** 수강·모집 종료일(YYYY-MM-DD 또는 ISO 접두) — 당일 23:59:59.999까지는 미종료 */
function isPastListEndYmd(endYmdOrIso: string | undefined): boolean {
	if (!endYmdOrIso) return false;
	const ymd = endYmdOrIso.slice(0, 10);
	const parts = ymd.split('-').map((x) => parseInt(x, 10));
	if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return false;
	const [y, m, d] = parts;
	const endLocal = new Date(y, m - 1, d, 23, 59, 59, 999);
	return Date.now() > endLocal.getTime();
}

/**
 * GET /class/open/list 한 행이 일반 사용자 목록에서 제외돼야 하는지(비활성·기간·상태).
 * 백엔드가 필터하지 않을 때 프론트 보조.
 */
export function isOpenClassListEntryEnded(row: OpenClassListItemDto & Record<string, unknown>): boolean {
	const r = row;
	if (r.isActive === false || r.is_active === false) return true;

	const endYmd =
		coerceClassDateToYyyyMmDd(r.classEndDate as string | null | undefined) ||
		coerceClassDateToYyyyMmDd(r.class_end_date as string | null | undefined) ||
		coerceClassDateToYyyyMmDd(r.endDate as string | null | undefined) ||
		coerceClassDateToYyyyMmDd(r.expiresAt as string | null | undefined) ||
		coerceClassDateToYyyyMmDd(r.expires_at as string | null | undefined);
	if (endYmd && isPastListEndYmd(endYmd)) return true;

	const recruitYmd =
		coerceClassDateToYyyyMmDd(r.recruitmentEndDate as string | null | undefined) ||
		coerceClassDateToYyyyMmDd(r.recruitment_end_date as string | null | undefined);
	if (recruitYmd && isPastListEndYmd(recruitYmd)) return true;

	const st = String(r.status ?? r.classStatus ?? r.class_status ?? '')
		.trim()
		.toUpperCase();
	if (st === 'EXPIRED' || st === 'ENDED' || st === 'CLOSED' || st === 'CANCELLED') return true;

	return false;
}

function readClassItemTranslations(row: Record<string, unknown>): ClassItem['translations'] | undefined {
	const raw = row.translations ?? row.classTranslations ?? row.class_translations;
	if (!Array.isArray(raw)) return undefined;
	const out: NonNullable<ClassItem['translations']> = {};
	for (const entry of raw) {
		if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
		const r = entry as Record<string, unknown>;
		const lang = normalizeLocaleTag(typeof r.language === 'string' ? r.language : undefined);
		if (lang !== 'ko' && lang !== 'en' && lang !== 'ar') continue;
		const title = typeof r.title === 'string' ? r.title.trim() : '';
		const description = typeof r.description === 'string' ? r.description.trim() : '';
		if (!title && !description) continue;
		out[lang] = {
			...(title ? { title } : {}),
			...(description ? { description } : {}),
		};
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * GET /class/open/list 항목 → ClassItem (오픈클래스 카드 UI)
 */
export function openClassListItemDtoToClassItem(dto: OpenClassListItemDto): ClassItem {
	const r = dto as OpenClassListItemDto & Record<string, unknown>;
	const isActiveRaw = r.isActive ?? r.is_active;
	let isActive = true;
	if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
	else if (typeof isActiveRaw === 'number') isActive = isActiveRaw !== 0;
	else if (typeof isActiveRaw === 'string') {
		const s = isActiveRaw.trim().toLowerCase();
		if (s === 'false' || s === '0') isActive = false;
	}

	const priceFromLegacy =
		dto.price != null && typeof dto.price === 'number' && Number.isFinite(dto.price) ? dto.price : undefined;
	const disc = coerceMonetaryLeaf(r.discountedPrice) ?? coerceMonetaryLeaf(r.discounted_price);
	const reg = coerceMonetaryLeaf(r.regularPrice) ?? coerceMonetaryLeaf(r.regular_price);
	let price: number | undefined = priceFromLegacy;
	let discountPrice: number | undefined;
	if (reg != null && disc != null && disc < reg) {
		price = reg;
		discountPrice = disc;
	} else if (reg == null && disc != null) {
		price = disc;
	} else if (reg != null && disc == null) {
		price = reg;
	}

	const stubCurriculum: CurriculumItem[] = [
		{ id: 0, code: 'open', name: '', duration: '-', platform: 'both' },
	];

	const pickOptStr = (v: unknown): string | undefined => {
		if (v == null) return undefined;
		const s = String(v).trim();
		return s || undefined;
	};
	/** 목록 API가 classStartDate 대신 startDate·learningStartDate 등으로 줄 때 */
	const pickYmd = (...vals: unknown[]): string | undefined => {
		for (const v of vals) {
			const y = coerceClassDateToYyyyMmDd(v as string | null | undefined);
			if (y) return y;
		}
		return undefined;
	};
	const cs = pickYmd(
		r.classStartDate,
		r.class_start_date,
		r.startDate,
		r.start_date,
		r.learningStartDate,
		r.learning_start_date
	);
	const ce = pickYmd(
		r.classEndDate,
		r.class_end_date,
		r.endDate,
		r.end_date,
		r.learningEndDate,
		r.learning_end_date
	);
	const rs = pickYmd(
		r.recruitmentStartDate,
		r.recruitment_start_date,
		r.recruitStartDate,
		r.recruit_start_date
	);
	const re = pickYmd(r.recruitmentEndDate, r.recruitment_end_date, r.recruitEndDate, r.recruit_end_date);

	const onlineUrl = pickOptStr(r.onlineUrl ?? r.online_url);
	const onlinePlatform = pickOptStr(r.onlinePlatform ?? r.online_platform);
	const educationVenue = pickOptStr(r.educationVenue ?? r.education_venue);
	const educationSchedule = pickOptStr(r.educationSchedule ?? r.education_schedule);
	const participationMethod = pickOptStr(r.participationMethod ?? r.participation_method);
	const isOnlineRaw = r.isOnlineClass ?? r.is_online_class;
	const isOnlineClass =
		typeof isOnlineRaw === 'boolean'
			? isOnlineRaw
			: typeof isOnlineRaw === 'string'
				? ['true', '1', 'yes'].includes(isOnlineRaw.trim().toLowerCase())
					? true
					: ['false', '0', 'no'].includes(isOnlineRaw.trim().toLowerCase())
						? false
						: undefined
				: undefined;

	let vpRaw: unknown = r.validityPeriod ?? r.validity_period;
	let vuRaw: unknown = r.validityPeriodUnit ?? r.validity_period_unit;
	const nestedPp = r.productPrice ?? r.product_price;
	if (nestedPp != null && typeof nestedPp === 'object' && !Array.isArray(nestedPp)) {
		const pp = nestedPp as Record<string, unknown>;
		if (vpRaw == null || vpRaw === '') vpRaw = pp.validityPeriod ?? pp.validity_period;
		if (vuRaw == null || vuRaw === '') vuRaw = pp.validityPeriodUnit ?? pp.validity_period_unit;
	}
	const vpNum = coerceNonNegativeInt(vpRaw as string | number | null | undefined);
	const vuStr =
		typeof vuRaw === 'string' && vuRaw.trim()
			? vuRaw.trim()
			: vuRaw != null && String(vuRaw).trim()
				? String(vuRaw).trim()
				: undefined;
	const inferredOnline =
		isOnlineClass === true || inferModeFromParticipationMethodField(participationMethod) === 'online';
	let openListOnlineAccessPeriodLabel: string | undefined;
	if (inferredOnline && vpNum != null && vpNum > 0) {
		openListOnlineAccessPeriodLabel = formatProductPriceValidityLabel(vpNum, vuStr);
	}
	const translations = readClassItemTranslations(r);

	let recruitmentPeriod: ClassItem['recruitmentPeriod'];
	if (rs && re) recruitmentPeriod = { startDate: rs, endDate: re };
	else if (re && !rs) recruitmentPeriod = { startDate: re, endDate: re };
	else if (rs && !re) recruitmentPeriod = { startDate: rs, endDate: rs };

	let participationPeriod: ClassItem['participationPeriod'];
	if (cs && ce) participationPeriod = { startDate: cs, endDate: ce };
	else if (ce && !cs) participationPeriod = { startDate: ce, endDate: ce };
	else if (cs && !ce) participationPeriod = { startDate: cs, endDate: '' };

	return {
		id: String(dto.classId),
		type: 'course',
		title: dto.title ?? '',
		subtitle: '',
		description: '',
		thumbnail:
			resolveThumbnailImageUrlFromRecord(r) ??
			(typeof dto.thumbnailImageUrl === 'string' && dto.thumbnailImageUrl.trim()
				? dto.thumbnailImageUrl.trim()
				: undefined),
		curriculum: stubCurriculum,
		classType: 'open',
		organizationName: dto.partnerOrganizationTitle?.trim() || dto.organizationTitle?.trim() || undefined,
		...(translations ? { translations } : {}),
		...(price != null ? { price } : {}),
		...(discountPrice != null ? { discountPrice } : {}),
		isActive,
		isForSale: (price != null && price > 0) || (discountPrice != null && discountPrice > 0),
		apiEnrollment: dto.isEnrolled,
		...(onlineUrl ? { onlineUrl } : {}),
		...(onlinePlatform ? { onlinePlatform } : {}),
		...(educationVenue ? { educationVenue } : {}),
		...(educationSchedule ? { educationSchedule } : {}),
		...(participationMethod ? { participationMethod } : {}),
		...(isOnlineClass !== undefined ? { isOnlineClass } : {}),
		...(openListOnlineAccessPeriodLabel ? { openListOnlineAccessPeriodLabel } : {}),
		...(recruitmentPeriod ? { recruitmentPeriod } : {}),
		...(participationPeriod ? { participationPeriod } : {}),
		...(cs ? { courseStartDate: cs } : {}),
		...(ce ? { courseEndDate: ce } : {}),
	};
}

/** Calendar `yyyy-MM-DD` → 로케일 규칙에 맞는 짧은 날짜 표기 (카드·주문·목록 공통) */
export function formatClassDateKoreanLong(ymd: string, locale?: string): string {
	const coerced = coerceClassDateToYyyyMmDd(ymd);
	if (!coerced) return ymd;
	return formatLocaleDateShort(coerced, locale);
}

export type OpenClassCardParticipationMode = 'online' | 'offline' | 'unknown';

/** API participationMethod 문자열 → 카드 모드 (알 수 없으면 undefined) */
function inferModeFromParticipationMethodField(raw: string | undefined): OpenClassCardParticipationMode | undefined {
	if (!raw?.trim()) return undefined;
	const s = raw.trim();
	const up = s.toUpperCase().replace(/\s+/g, '_');
	if (['OFFLINE', 'IN_PERSON', 'INPERSON', 'FACE_TO_FACE', 'FACE2FACE', 'ONSITE', 'OFF_LINE'].includes(up)) {
		return 'offline';
	}
	if (['ONLINE', 'REMOTE', 'WEB', 'VIRTUAL', 'E_LEARNING', 'ELEARNING'].includes(up)) {
		return 'online';
	}
	if (/대면|오프라인|현장/.test(s)) return 'offline';
	if (/온라인|비대면|원격/.test(s)) return 'online';
	return undefined;
}

/**
 * 제목·부제·설명·일정에서 대면/온라인 힌트.
 * - `비대면`이 있으면 `대면` 부분 매칭을 피하기 위해 먼저 제외
 */
function textHintsOfflineFromClassItem(item: ClassItem): boolean {
	const blob = [item.title, item.subtitle, item.description, item.educationSchedule]
		.map((x) => (typeof x === 'string' ? x : ''))
		.join('\n');
	if (!blob.trim()) return false;
	if (/비대면/.test(blob)) return false;
	if (/대면/.test(blob)) return true;
	if (/오프라인|현장\s*교육|in[\s-]*person|face[\s-]*to[\s-]*face|on[\s-]*site/i.test(blob)) return true;
	return false;
}

function textHintsOnlineFromClassItem(item: ClassItem): boolean {
	const blob = [item.title, item.subtitle, item.description, item.educationSchedule]
		.map((x) => (typeof x === 'string' ? x : ''))
		.join('\n');
	if (!blob.trim()) return false;
	if (/비대면|온라인|원격|virtual|online(\s|$)/i.test(blob)) return true;
	return false;
}

/**
 * `onlinePlatform`에 VR·모바일·PC 등만 있는 경우 = 실습 기기(대면 현장)로 보고 원격 수업으로 취급하지 않음.
 * (원격은 보통 onlineUrl 또는 Zoom/Teams 등 문자열이 함께 옴)
 */
function isLikelySimulationDevicePlatformOnly(platform: string): boolean {
	const u = platform.trim().toUpperCase().replace(/[\s_-]+/g, '');
	if (!u) return false;
	if (['VR', 'MOBILE', 'PC', 'TABLET', 'ALL', 'BOTH', 'IPHONE', 'ANDROID', 'IPAD', 'WEBGL'].includes(u)) return true;
	if (/^(VR|PC|MOBILE|TABLET)(APP)?$/.test(u)) return true;
	return false;
}

/** 오픈클래스 카드 — 온라인/대면 추정 (`isOnlineClass`·참여방법·장소 우선, 기기 플랫폼만으로는 원격 판정 안 함) */
export function inferOpenClassListParticipationMode(item: ClassItem): OpenClassCardParticipationMode {
	if (item.isOnlineClass === false) return 'offline';
	if (item.isOnlineClass === true) return 'online';

	const fromField = inferModeFromParticipationMethodField(item.participationMethod);
	if (fromField) return fromField;

	if (item.educationVenue?.trim()) return 'offline';

	if (textHintsOfflineFromClassItem(item)) return 'offline';

	const ou = item.onlineUrl?.trim();
	if (ou) return 'online';

	const op = item.onlinePlatform?.trim();
	if (op && !isLikelySimulationDevicePlatformOnly(op)) return 'online';

	if (item.educationSchedule?.trim()) return 'offline';

	if (textHintsOnlineFromClassItem(item)) return 'online';

	/** 수강·모집 기간만으로는 원격/대면 구분 불가 — 기기 플랫폼만 있으면 대면 현장 실습으로 표시 */
	if (op && isLikelySimulationDevicePlatformOnly(op)) return 'offline';

	return 'unknown';
}

/**
 * 오픈클래스 카드 «수강기간» 한 줄 (일자가 있으면 온라인·대면 동일: 시작~종료를 로케일 날짜로, 없으면 온라인 전용 라벨 등)
 * 빈 문자열이면 UI에서 `—` 처리
 */
function openListRecruitmentYmdPair(item: ClassItem): { ra: string | undefined; rb: string | undefined } {
	const rp = item.recruitmentPeriod;
	const ra = rp?.startDate ? coerceClassDateToYyyyMmDd(rp.startDate) : undefined;
	const rb = rp?.endDate ? coerceClassDateToYyyyMmDd(rp.endDate) : undefined;
	return { ra, rb };
}

export function getOpenClassListCoursePeriodLine(item: ClassItem, mode: OpenClassCardParticipationMode, locale?: string): string {
	if (mode === 'online') {
		/** 온라인도 대면과 동일: 확정 일자가 있으면 로케일 형식 범위(`… - …`), 없으면 라벨·모집기간 범위, 최후에만 일수 문자열 */
		const cs =
			coerceClassDateToYyyyMmDd(item.courseStartDate) ||
			coerceClassDateToYyyyMmDd(item.participationPeriod?.startDate);
		const ce =
			coerceClassDateToYyyyMmDd(item.courseEndDate) ||
			coerceClassDateToYyyyMmDd(item.participationPeriod?.endDate);
		if (cs && ce) return `${formatClassDateKoreanLong(cs, locale)} - ${formatClassDateKoreanLong(ce, locale)}`;
		if (cs && !ce) return formatClassDateKoreanLong(cs, locale);
		if (ce && !cs) return formatClassDateKoreanLong(ce, locale);

		const pl = item.openListOnlineAccessPeriodLabel?.trim();
		if (pl) return pl;

		const { ra, rb } = openListRecruitmentYmdPair(item);
		if (ra && rb) return `${formatClassDateKoreanLong(ra, locale)} - ${formatClassDateKoreanLong(rb, locale)}`;
		if (ra && !rb) return formatClassDateKoreanLong(ra, locale);
		if (rb && !ra) return formatClassDateKoreanLong(rb, locale);

		return '';
	}
	if (mode === 'offline') {
		/** 대면 수강기간: GET /class/open/list 의 classStartDate·classEndDate → courseStartDate / participationPeriod (모집기간 아님) */
		const cs =
			coerceClassDateToYyyyMmDd(item.courseStartDate) ||
			coerceClassDateToYyyyMmDd(item.participationPeriod?.startDate);
		const ce =
			coerceClassDateToYyyyMmDd(item.courseEndDate) ||
			coerceClassDateToYyyyMmDd(item.participationPeriod?.endDate);
		if (cs && ce) return `${formatClassDateKoreanLong(cs, locale)} - ${formatClassDateKoreanLong(ce, locale)}`;
		const rp = item.recruitmentPeriod;
		const a = rp?.startDate ? coerceClassDateToYyyyMmDd(rp.startDate) : undefined;
		const b = rp?.endDate ? coerceClassDateToYyyyMmDd(rp.endDate) : undefined;
		if (a && b) return `${formatClassDateKoreanLong(a, locale)} - ${formatClassDateKoreanLong(b, locale)}`;
		return '';
	}
	/* unknown: 가능한 범위에서 표시 (수강·모집 일자는 대면/온라인과 동일하게 한글 날짜 범위 우선) */
	const cs =
		coerceClassDateToYyyyMmDd(item.courseStartDate) ||
		coerceClassDateToYyyyMmDd(item.participationPeriod?.startDate);
	const ce =
		coerceClassDateToYyyyMmDd(item.courseEndDate) ||
		coerceClassDateToYyyyMmDd(item.participationPeriod?.endDate);
	if (cs && ce) return `${formatClassDateKoreanLong(cs, locale)} - ${formatClassDateKoreanLong(ce, locale)}`;
	if (cs && !ce) return formatClassDateKoreanLong(cs, locale);
	if (ce && !cs) return formatClassDateKoreanLong(ce, locale);
	const rp = item.recruitmentPeriod;
	const ra = rp?.startDate ? coerceClassDateToYyyyMmDd(rp.startDate) : undefined;
	const rb = rp?.endDate ? coerceClassDateToYyyyMmDd(rp.endDate) : undefined;
	if (ra && rb) return `${formatClassDateKoreanLong(ra, locale)} - ${formatClassDateKoreanLong(rb, locale)}`;
	if (ra && !rb) return formatClassDateKoreanLong(ra, locale);
	if (rb && !ra) return formatClassDateKoreanLong(rb, locale);
	return '';
}

/** enrolled/list 행에서 배지용 status — API가 누락·snake_case일 때 보조 */
function deriveEnrolledClassStatus(row: EnrolledClassListItemDto & Record<string, unknown>): EnrolledClassStatus {
	const raw = row.status ?? row.Status;
	if (raw === 'AVAILABLE' || raw === 'IN_PROGRESS' || raw === 'COMPLETED' || raw === 'EXPIRED') {
		return raw;
	}
	if (row.isCompleted === true) return 'COMPLETED';
	const endStr =
		coerceClassDateToYyyyMmDd(row.classEndDate) ||
		coerceClassDateToYyyyMmDd(row.class_end_date as string | undefined) ||
		coerceClassDateToYyyyMmDd(row.expiresAt) ||
		coerceClassDateToYyyyMmDd(row.expires_at as string | undefined);
	if (endStr) {
		const ymd = endStr.slice(0, 10);
		const parts = ymd.split('-').map((x) => parseInt(x, 10));
		if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
			const [y, m, d] = parts;
			const endLocal = new Date(y, m - 1, d, 23, 59, 59, 999);
			if (Date.now() > endLocal.getTime()) return 'EXPIRED';
		}
	}
	return 'IN_PROGRESS';
}

/**
 * GET /class/enrolled/list 항목 → ClassItem (마이클래스 카드 UI)
 */
export function enrolledClassListItemDtoToClassItem(dto: EnrolledClassListItemDto): ClassItem {
	const r = dto as EnrolledClassListItemDto & Record<string, unknown>;
	const derivedStatus = deriveEnrolledClassStatus(r);
	/**
	 * 수강기간: 클래스 수강 시작·종료일(classStartDate/classEndDate) 우선.
	 * 각각 null 이면 개인 등록일(enrolledAt)·개인 만료일(expiresAt)로 대체한다.
	 * (종료 판단은 백엔드·isMyEnrollmentEnded 와 동일하게 classEndDate → expiresAt 순)
	 */
	const classStartYmd = coerceClassDateToYyyyMmDd(dto.classStartDate as string | null | undefined);
	const classEndYmd = coerceClassDateToYyyyMmDd(dto.classEndDate as string | null | undefined);
	const enrolledYmd = coerceClassDateToYyyyMmDd(dto.enrolledAt as string | null | undefined);
	const expiresYmd = coerceClassDateToYyyyMmDd(dto.expiresAt as string | null | undefined);

	const participationStartYmd = classStartYmd || enrolledYmd;
	const participationEndYmd = classEndYmd || expiresYmd;

	let participationPeriod: { startDate: string; endDate: string } | undefined;
	if (participationStartYmd || participationEndYmd) {
		participationPeriod = {
			startDate: participationStartYmd || participationEndYmd,
			endDate: participationEndYmd,
		};
	}
	const rawThumb = dto.thumbnailImageUrl as unknown;
	const thumbnail =
		typeof rawThumb === 'string' && rawThumb.trim()
			? rawThumb.trim()
			: rawThumb && typeof rawThumb === 'object' && rawThumb !== null && 'url' in rawThumb
				? String((rawThumb as { url?: string }).url ?? '').trim() || undefined
				: undefined;
	const n = coerceNonNegativeInt(dto.scenarioCount) ?? 0;
	return {
		id: String(dto.classId),
		type: 'course',
		title: dto.title,
		subtitle: '',
		description: '',
		thumbnail,
		curriculum: Array.from({ length: n }, (_, i) => ({
			id: i + 1,
			code: `s-${i + 1}`,
			name: '',
			duration: '-',
			platform: 'both',
		})),
		classType: dto.isOpenClass ? 'open' : 'my',
		participationPeriod,
		isActive: dto.isActive,
		enrolledApiStatus: derivedStatus,
	};
}

/** 숫자·금액 문자열·{ amount } 등 한두 단계 중첩까지 */
function coerceMonetaryLeaf(val: unknown, depth = 0): number | undefined {
	if (depth > 5) return undefined;
	if (typeof val === 'number' && Number.isFinite(val) && val >= 0) return val;
	if (typeof val === 'string' && val.trim()) {
		const n = Number(val.replace(/,/g, '').trim());
		if (Number.isFinite(n) && n >= 0) return n;
	}
	if (val && typeof val === 'object' && !Array.isArray(val)) {
		const o = val as Record<string, unknown>;
		for (const innerK of [
			'amount',
			'value',
			'number',
			'price',
			'listPrice',
			'list_price',
		] as const) {
			const u = coerceMonetaryLeaf(o[innerK], depth + 1);
			if (u != null) return u;
		}
	}
	return undefined;
}

/** 상세 API에 Swagger에 없는 가격 필드가 올 때 대비 */
function pickDetailPriceField(r: Record<string, unknown>, keys: readonly string[]): number | undefined {
	for (const k of keys) {
		const v = r[k];
		const n = coerceMonetaryLeaf(v);
		if (n != null) return n;
	}
	return undefined;
}

const PRODUCT_PRICE_AMOUNT_KEYS = [
	'amount',
	'price',
	'listPrice',
	'salePrice',
	'originalPrice',
	'regularPrice',
	'unitPrice',
	'sellingPrice',
	'basePrice',
	'list_price',
	'sale_price',
	'original_price',
	'regular_price',
	'unit_price',
	'selling_price',
	'base_price',
] as const;

const PRODUCT_PRICE_DISCOUNT_KEYS = [
	'discountPrice',
	'salePrice',
	'discountedPrice',
	'discount_price',
	'sale_price',
	'discounted_price',
] as const;

/** GET /class/{id}/detail — 최상위·productPrice·product 중첩에서 정가/판매가 후보 추출 */
function pickClassDetailListPrice(r: Record<string, unknown>): number | undefined {
	/** 상세 전용 정가 — 일반 `price`보다 우선(결제 정가·취소선 표시) */
	const canonicalRegular = pickDetailPriceField(r, ['regularPrice', 'regular_price']);
	if (canonicalRegular != null) return canonicalRegular;

	const top = pickDetailPriceField(r, [
		'price',
		'listPrice',
		'originalPrice',
		'regularPrice',
		'classPrice',
		'amount',
		'totalPrice',
		'unitPrice',
		'sellingPrice',
		'basePrice',
		'list_price',
		'original_price',
		'regular_price',
		'class_price',
		'total_price',
		'unit_price',
		'selling_price',
		'base_price',
	]);
	if (top != null) return top;

	for (const key of ['productPrice', 'product_price', 'productPrices', 'product_prices'] as const) {
		const nested = r[key];
		if (Array.isArray(nested) && nested.length > 0) {
			const first = nested[0];
			if (first && typeof first === 'object' && !Array.isArray(first)) {
				const n = pickDetailPriceField(first as Record<string, unknown>, PRODUCT_PRICE_AMOUNT_KEYS);
				if (n != null) return n;
			}
		}
		if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
			const o = nested as Record<string, unknown>;
			const n = pickDetailPriceField(o, PRODUCT_PRICE_AMOUNT_KEYS);
			if (n != null) return n;
		}
	}

	for (const key of ['product', 'productDto', 'product_detail'] as const) {
		const nested = r[key];
		if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
			const o = nested as Record<string, unknown>;
			const n = pickDetailPriceField(o, [
				'price',
				'listPrice',
				'regularPrice',
				'defaultPrice',
				'amount',
				'list_price',
				'regular_price',
				'default_price',
			]);
			if (n != null) return n;
		}
	}

	return undefined;
}

function pickClassDetailDiscountPrice(r: Record<string, unknown>): number | undefined {
	/** 상세 전용 실결제가 — product 중첩보다 우선 */
	const canonicalDiscounted = pickDetailPriceField(r, ['discountedPrice', 'discounted_price']);
	if (canonicalDiscounted != null) return canonicalDiscounted;

	const top = pickDetailPriceField(r, [
		'discountPrice',
		'salePrice',
		'discountedPrice',
		'discount_price',
		'sale_price',
		'discounted_price',
	]);
	if (top != null) return top;

	for (const key of ['productPrice', 'product_price', 'productPrices', 'product_prices'] as const) {
		const nested = r[key];
		if (Array.isArray(nested) && nested.length > 0) {
			const first = nested[0];
			if (first && typeof first === 'object' && !Array.isArray(first)) {
				const n = pickDetailPriceField(first as Record<string, unknown>, PRODUCT_PRICE_DISCOUNT_KEYS);
				if (n != null) return n;
			}
		}
		if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
			const o = nested as Record<string, unknown>;
			const n = pickDetailPriceField(o, PRODUCT_PRICE_DISCOUNT_KEYS);
			if (n != null) return n;
		}
	}
	return undefined;
}

function pickStringFromRecord(obj: Record<string, unknown>, keys: readonly string[]): string | undefined {
	for (const k of keys) {
		const v = obj[k];
		if (typeof v === 'string' && v.trim()) return v.trim();
	}
	return undefined;
}

const LECTURE_VIDEO_URL_KEYS = [
	'lectureVideoUrl',
	'lecture_video_url',
	'lectureVideoURL',
	'videoUrl',
	'video_url',
	'videoLink',
	'video_link',
	'contentUrl',
	'content_url',
	'youtubeUrl',
	'youtube_url',
	'mediaUrl',
	'media_url',
	'link',
	'url',
] as const;

const NESTED_CLASS_ITEM_KEYS = [
	'lecture',
	'lectureInfo',
	'lectureDetail',
	'lecture_detail',
	'classItem',
	'scenario',
	'scenarioInfo',
	'scenario_info',
] as const;

/** API가 lecture 등으로 감싼 필드를 펼쳐 URL·ID 픽 */
function flattenNestedItemFields(obj: Record<string, unknown>): Record<string, unknown> {
	const out: Record<string, unknown> = { ...obj };
	for (const nk of NESTED_CLASS_ITEM_KEYS) {
		const v = obj[nk];
		if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
		const inner = v as Record<string, unknown>;
		for (const [ik, iv] of Object.entries(inner)) {
			const cur = out[ik];
			if (cur === undefined || cur === null || cur === '') {
				out[ik] = iv;
			}
		}
	}
	return out;
}

/** 클래스 아이템별 예상 재생 시간(초) (API 문서 기준: scenarioExpectedPlayTimeSeconds, SCENARIO 타입에만 존재) */
export function pickClassItemExpectedPlaySeconds(s: ClassItemInfoDto & Record<string, unknown>): number | undefined {
	const sec = coerceNonNegativeInt(s.scenarioExpectedPlayTimeSeconds);
	return sec != null && sec > 0 ? sec : undefined;
}

/** 클래스 아이템 DTO에서 강의 동영상 URL (camelCase·snake_case·별칭·중첩 lecture) */
export function pickLectureVideoUrlFromDto(s: ClassItemInfoDto & Record<string, unknown>): string | undefined {
	const flat = flattenNestedItemFields(s as Record<string, unknown>);
	return pickStringFromRecord(flat, LECTURE_VIDEO_URL_KEYS);
}

function inferClassItemIsVideoLecture(s: ClassItemInfoDto, flat: Record<string, unknown>): boolean {
	const upper = String(s.type ?? '')
		.trim()
		.toUpperCase()
		.replace(/\s+/g, '_')
		.replace(/-/g, '_');
	if (['LECTURE', 'VIDEO', 'LECTURE_VIDEO', 'YOUTUBE', 'MEDIA', 'VOD'].includes(upper)) return true;
	if (['SCENARIO', 'SIMULATION', 'SIM'].includes(upper)) return false;
	const scenarioId = coercePositiveInt(s.scenarioId);
	if (scenarioId != null) return false;
	const mediaUrl = pickStringFromRecord(flat, LECTURE_VIDEO_URL_KEYS);
	return !!mediaUrl;
}

/** 커리큘럼 라우트용 아이템 ID (API 문서 기준: lectureId 또는 scenarioId) */
function pickCurriculumNavId(s: ClassItemInfoDto & Record<string, unknown>, idx: number, isLecture: boolean): number {
	if (isLecture) {
		const lectureId = coercePositiveInt(s.lectureId);
		return lectureId ?? idx + 1;
	}
	const scenarioId = coercePositiveInt(s.scenarioId);
	return scenarioId ?? idx + 1;
}

/**
 * 시나리오 커리큘럼 행 — 실습모드명(표시)
 * (GET /class/detail `classItemList`·중첩 `scenario` / `modeValueList` / snake_case)
 */
function pickClassItemPracticeModeName(
	s: ClassItemInfoDto & Record<string, unknown>,
	flat: Record<string, unknown>
): string | undefined {
	// scenarioModeValueTitle만 확인
	const title = flat.scenarioModeValueTitle ?? flat.scenario_mode_value_title;
	if (typeof title === 'string' && title.trim()) {
		return title.trim();
	}
	return undefined;
}

/** YouTube 표준 비디오 ID (대부분 11자, 구형 10~12자 허용) */
const YOUTUBE_VIDEO_ID_RE = /^[a-zA-Z0-9_-]{10,12}$/;

function tryDecodeURIComponentSegment(s: string): string {
	try {
		return decodeURIComponent(s.replace(/\+/g, ' '));
	} catch {
		return s;
	}
}

function isLikelyYoutubeVideoId(id: string): boolean {
	return YOUTUBE_VIDEO_ID_RE.test(id);
}

/**
 * YouTube watch / Shorts / youtu.be → 비디오 ID (playlist·si 등 부가 쿼리 무시)
 * — ReactPlayer에 watch+list URL을 넘기면 URL이 바뀔 때마다 재로드·Invalid video id가 날 수 있음
 */
export function getYouTubeVideoIdForEmbed(rawUrl: string): string | null {
	const cleaned = rawUrl.trim().replace(/^\uFEFF/, '').replace(/\u200b/g, '');
	if (!cleaned) return null;

	const fromYouTuBe = cleaned.match(/youtu\.be\/([a-zA-Z0-9_-]{10,12})(?:\?|#|\/|$)/i);
	if (fromYouTuBe && isLikelyYoutubeVideoId(fromYouTuBe[1])) {
		return tryDecodeURIComponentSegment(fromYouTuBe[1]);
	}

	const fromWatchV = cleaned.match(/[?&]v=([a-zA-Z0-9_-]{10,12})(?:&|#|\/|$)/i);
	if (fromWatchV && isLikelyYoutubeVideoId(fromWatchV[1])) {
		return tryDecodeURIComponentSegment(fromWatchV[1]);
	}

	const fromEmbed = cleaned.match(/\/embed\/([a-zA-Z0-9_-]{10,12})(?:\?|#|\/|$)/i);
	if (fromEmbed && isLikelyYoutubeVideoId(fromEmbed[1])) {
		return tryDecodeURIComponentSegment(fromEmbed[1]);
	}

	const fromShorts = cleaned.match(/\/shorts\/([a-zA-Z0-9_-]{10,12})(?:\?|#|\/|$)/i);
	if (fromShorts && isLikelyYoutubeVideoId(fromShorts[1])) {
		return tryDecodeURIComponentSegment(fromShorts[1]);
	}

	const url = normalizeVideoPlaybackUrl(cleaned);
	try {
		const u = new URL(url.startsWith('//') ? `https:${url}` : url);
		const host = u.hostname.replace(/^www\./i, '').toLowerCase();
		if (host === 'youtu.be') {
			const id = u.pathname.split('/').filter(Boolean)[0]?.split('?')[0] ?? '';
			if (isLikelyYoutubeVideoId(id)) return tryDecodeURIComponentSegment(id);
		}
		if (
			host.endsWith('youtube.com') ||
			host === 'youtube-nocookie.com' ||
			host === 'm.youtube.com' ||
			host === 'music.youtube.com'
		) {
			if (u.pathname.startsWith('/embed/')) {
				const id = u.pathname.slice('/embed/'.length).split('/')[0]?.split('?')[0] ?? '';
				if (isLikelyYoutubeVideoId(id)) return tryDecodeURIComponentSegment(id);
			}
			if (u.pathname.startsWith('/shorts/')) {
				const id = u.pathname.slice('/shorts/'.length).split('/')[0]?.split('?')[0] ?? '';
				if (isLikelyYoutubeVideoId(id)) return tryDecodeURIComponentSegment(id);
			}
			const v = u.searchParams.get('v');
			if (v && isLikelyYoutubeVideoId(v)) return tryDecodeURIComponentSegment(v);
		}
	} catch {
		/* ignore */
	}
	return null;
}

/** iframe src (항상 /embed/ + ID만 사용) */
export function buildYoutubeIframeSrc(videoId: string): string {
	const id = encodeURIComponent(videoId);
	return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
}

/** 스킴 없는 URL에 https 보정 (공유 링크·상대 프로토콜) */
export function normalizeVideoPlaybackUrl(raw: string): string {
	const t = raw.trim();
	if (!t) return t;
	if (/^https?:\/\//i.test(t)) return t;
	if (t.startsWith('//')) return `https:${t}`;
	return `https://${t}`;
}

/** 커리큘럼 항목에서 재생용 동영상 URL */
export function resolveCurriculumItemVideoUrl(
	item: CurriculumItem & { data?: unknown } & Record<string, unknown>
): string | null {
	const fromTop = typeof item.url === 'string' ? item.url.trim() : '';
	if (fromTop) return normalizeVideoPlaybackUrl(fromTop);
	const data = item.data as { url?: string } | undefined;
	if (data && typeof data.url === 'string' && data.url.trim()) {
		return normalizeVideoPlaybackUrl(data.url.trim());
	}
	const loose = pickStringFromRecord(item as Record<string, unknown>, LECTURE_VIDEO_URL_KEYS);
	return loose ? normalizeVideoPlaybackUrl(loose) : null;
}

/** YouTube 호스트 여부 (react-player YouTube 프리뷰용) */
export function isYoutubePlaybackUrl(url: string): boolean {
	const u = url.trim();
	if (!u) return false;
	try {
		const normalized = /^https?:\/\//i.test(u) || u.startsWith('//') ? u : `https://${u}`;
		const parsed = new URL(u.startsWith('//') ? `https:${u}` : normalized);
		const h = parsed.hostname.replace(/^www\./i, '').toLowerCase();
		return (
			h === 'youtube.com' ||
			h === 'youtu.be' ||
			h === 'm.youtube.com' ||
			h === 'youtube-nocookie.com' ||
			h === 'music.youtube.com'
		);
	} catch {
		return /youtube\.com|youtu\.be/i.test(u);
	}
}

/**
 * 클래스 상세 응답에서 시나리오/강의 배열 추출
 * — OpenAPI는 `classItemList`, 일부 응답은 `itemList` 또는 snake_case
 */
export function getClassDetailItemList(
	dto: GetClassDetailResponseDto & Record<string, unknown>
): ClassItemInfoDto[] {
	const snake = dto as Record<string, unknown>;
	const candidates: unknown[] = [
		dto.classItemList,
		dto.itemList,
		snake.class_item_list,
		snake.item_list,
	];
	const arrays = candidates.filter((x): x is unknown[] => Array.isArray(x));
	for (const arr of arrays) {
		if (arr.length > 0) return arr as ClassItemInfoDto[];
	}
	return (arrays[0] as ClassItemInfoDto[] | undefined) ?? [];
}

/**
 * GetClassDetailResponseDto → ClassItem (상세 화면용)
 */
export function classDetailDtoToClassItem(
	dto: GetClassDetailResponseDto,
	options?: { organizationId?: number; organizationName?: string }
): ClassItem {
	const r = dto as GetClassDetailResponseDto & Record<string, unknown>;
	const priceFromApi = pickClassDetailListPrice(r);
	const discountFromApi = pickClassDetailDiscountPrice(r);
	let price = priceFromApi;
	let discountPrice = discountFromApi;
	if (
		priceFromApi != null &&
		discountFromApi != null &&
		discountFromApi < priceFromApi
	) {
		discountPrice = discountFromApi;
		price = priceFromApi;
	} else if (priceFromApi == null && discountFromApi != null) {
		price = discountFromApi;
		discountPrice = undefined;
	}
	if (discountPrice != null && price != null && discountPrice >= price) {
		discountPrice = undefined;
	}
	const isForSale = (price != null && price > 0) || (discountPrice != null && discountPrice > 0);

	const itemList = getClassDetailItemList(dto as GetClassDetailResponseDto & Record<string, unknown>);
	const curriculum: CurriculumItem[] = itemList.map((s, idx) => {
		const flat = flattenNestedItemFields(s as unknown as Record<string, unknown>);
		const isLecture = inferClassItemIsVideoLecture(s, flat);
		const idNum = pickCurriculumNavId(s as ClassItemInfoDto & Record<string, unknown>, idx, isLecture);
		const name =
			s.title ??
			s.scenarioTitle ??
			s.lectureTitle ??
			(typeof flat.lectureTitle === 'string' && flat.lectureTitle.trim() ? flat.lectureTitle.trim() : undefined) ??
			(isLecture ? `강의 ${idNum}` : `시나리오 ${idNum}`);
		const lectureUrl = isLecture ? pickLectureVideoUrlFromDto(s as ClassItemInfoDto & Record<string, unknown>) : undefined;
		const playSec = pickClassItemExpectedPlaySeconds(s as ClassItemInfoDto & Record<string, unknown>);
		const durationMinutes =
			playSec != null && playSec > 0 ? Math.max(1, Math.ceil(playSec / 60)) : undefined;
		const duration = durationMinutes != null ? `${durationMinutes}분` : '-';
		const scenarioIdForPlay =
			!isLecture ? coercePositiveInt(s.scenarioId) : undefined;
		const practiceMode = !isLecture
			? pickClassItemPracticeModeName(s as ClassItemInfoDto & Record<string, unknown>, flat)
			: undefined;
		const modeValue = !isLecture
			? (s.scenarioModeValue as string | undefined)
			: undefined;
		return {
			id: idNum,
			code: isLecture ? `lec-${idNum}` : `sc-${idNum}`,
			name,
			duration,
			...(durationMinutes != null ? { durationMinutes } : {}),
			platform: 'both',
			type: isLecture ? 'video' : 'scenario',
			...(lectureUrl ? { url: normalizeVideoPlaybackUrl(lectureUrl) } : {}),
			...(scenarioIdForPlay != null ? { scenarioId: scenarioIdForPlay } : {}),
			...(practiceMode ? { practiceMode } : {}),
			...(modeValue ? { modeValue } : {}),
		};
	});

	const isOpenClass = dto.isOpenClass === true;
	/**
	 * isOnlineClass 누락 시: 오픈클래스만 기본 온라인(true), 기관 클래스는 false로 두어
	 * 수강기간·대면 UI가 잘못 분기되지 않게 함(undefined !== false 방지).
	 */
	const isOnline =
		dto.isOnlineClass === false ? false : dto.isOnlineClass === true ? true : isOpenClass;

	// 오픈클래스 온라인은 수강기간 없음, 그 외는 날짜 매핑
	const hasDateRange = !isOpenClass || !isOnline;
	const openInPerson = isOpenClass && !isOnline;
	const classStartYmd = coerceClassDateToYyyyMmDd(dto.classStartDate ?? r.class_start_date);
	const classEndYmd = coerceClassDateToYyyyMmDd(dto.classEndDate ?? r.class_end_date);
	const aliasStartYmd = coerceClassDateToYyyyMmDd(r.startDate);
	const aliasEndYmd = coerceClassDateToYyyyMmDd(r.endDate);
	let startYmd = classStartYmd;
	let endYmd = classEndYmd;
	if (hasDateRange && (!startYmd || !endYmd) && !openInPerson) {
		if (!startYmd && aliasStartYmd) startYmd = aliasStartYmd;
		/** 종료일이 없으면 시작일로 채우지 않고 빈값 유지 — 시작일이 지나는 순간 종료로 오판된다 */
		if (!endYmd) endYmd = aliasEndYmd || '';
	}
	const startDate = hasDateRange && startYmd ? startYmd : '';
	const endDate = hasDateRange && endYmd ? endYmd : '';

	// 모집기간: 대면(오픈 대면)인 경우만 매핑
	const recruitmentStartYmd = coerceClassDateToYyyyMmDd(dto.recruitmentStartDate);
	const recruitmentEndYmd = coerceClassDateToYyyyMmDd(dto.recruitmentEndDate);
	const recruitmentPeriod =
		!isOnline && recruitmentStartYmd
			? {
					startDate: recruitmentStartYmd,
					endDate: recruitmentEndYmd || recruitmentStartYmd,
				}
			: undefined;

	const totalItems = itemList.length;
	const minCount = dto.minScenarioCompletionCount ?? totalItems;
	/**
	 * 이수 목표 문구: `minCount === 커리큘럼 길이`만으로는「전체」vs「직접입력 N개」를 구분할 수 없음.
	 * 명시 플래그가 없으면 항상 개수 문구(시나리오 N개)를 쓰도록 requireAllScenarios 는 기본 false.
	 */
	const explicitRequireAllScenarios =
		r.requireAllScenarioCompletion === true ||
		r.require_all_scenario_completion === true ||
		r.requireAllScenarios === true ||
		r.require_all_scenarios === true ||
		String(r.minScenarioMode ?? r.min_scenario_mode ?? '')
			.trim()
			.toUpperCase() === 'ALL';

	const pickOptStrDetail = (v: unknown): string | undefined => {
		if (v == null) return undefined;
		const s = String(v).trim();
		return s || undefined;
	};
	const participationMethodStr = pickOptStrDetail(
		r.participationMethod ?? r.participation_method ?? dto.participationMethod
	);
	const participationInfoStr = pickOptStrDetail(
		r.participationInfo ??
			r.participation_info ??
			r.participationGuide ??
			r.participation_guide ??
			dto.participationGuide
	);
	const onlineUrlStr = pickOptStrDetail(r.onlineUrl ?? r.online_url ?? dto.onlineUrl);
	const educationScheduleStr = pickOptStrDetail(
		r.educationSchedule ?? r.education_schedule ?? dto.educationSchedule
	);

	let vpRaw: unknown = r.validityPeriod ?? r.validity_period ?? dto.validityPeriod;
	let vuRaw: unknown = r.validityPeriodUnit ?? r.validity_period_unit ?? dto.validityPeriodUnit;
	const nestedPpDetail = r.productPrice ?? r.product_price;
	if (nestedPpDetail != null && typeof nestedPpDetail === 'object' && !Array.isArray(nestedPpDetail)) {
		const pp = nestedPpDetail as Record<string, unknown>;
		if (vpRaw == null || vpRaw === '') vpRaw = pp.validityPeriod ?? pp.validity_period;
		if (vuRaw == null || vuRaw === '') vuRaw = pp.validityPeriodUnit ?? pp.validity_period_unit;
	}
	const vpNumDetail = coerceNonNegativeInt(vpRaw as string | number | null | undefined);
	const vuStrDetail =
		typeof vuRaw === 'string' && vuRaw.trim()
			? vuRaw.trim()
			: vuRaw != null && String(vuRaw).trim()
				? String(vuRaw).trim()
				: undefined;
	let openListOnlineAccessPeriodLabel: string | undefined;
	if (isOpenClass && isOnline && vpNumDetail != null && vpNumDetail > 0) {
		openListOnlineAccessPeriodLabel = formatProductPriceValidityLabel(vpNumDetail, vuStrDetail);
	}

	return {
		id: String(dto.classId),
		type: 'course',
		title: dto.title,
		subtitle: dto.description ?? '',
		description: dto.description ?? '',
		thumbnail: resolveThumbnailImageUrlFromRecord(r) ?? (typeof dto.thumbnailImageUrl === 'string' && dto.thumbnailImageUrl.trim()
			? dto.thumbnailImageUrl.trim()
			: undefined),
		curriculum,
		classType: isOpenClass ? 'open' : 'my',
		isActive: dto.isActive,
		// 온라인 오픈클래스는 수강기간 없음 → undefined
		participationPeriod: startDate ? { startDate, endDate } : undefined,
		recruitmentPeriod,
		// 대면 여부를 onlinePlatform/educationVenue 로 ClassDetail에 전달
		...(isOnline ? { onlinePlatform: 'online' } : { educationVenue: 'inPerson' }),
		completionRequirements: {
			minScenarios: minCount,
			minPassingScore: dto.minPassingScore ?? 0,
			requireAllScenarios: explicitRequireAllScenarios,
		},
		currentParticipants: dto.participantCount ?? 0,
		maxParticipants: dto.maxParticipants ?? 0,
		createdDate: startDate,
		organizationId: options?.organizationId ?? dto.organizationId,
		organizationName:
			options?.organizationName ??
			(dto.partnerOrganizationTitle?.trim() || dto.organizationTitle?.trim() || undefined),
		...(price != null ? { price } : {}),
		...(discountPrice != null ? { discountPrice } : {}),
		...(dto.discountTitle != null && typeof dto.discountTitle === 'string' && dto.discountTitle.trim() ? { discountTitle: dto.discountTitle.trim() } : {}),
		...(isForSale ? { isForSale } : {}),
		...(typeof dto.productPriceId === 'number' && dto.productPriceId > 0 ? { productPriceId: dto.productPriceId } : {}),
		...(isOpenClass ? { isOnlineClass: dto.isOnlineClass } : {}),
		...(participationMethodStr ? { participationMethod: participationMethodStr } : {}),
		...(participationInfoStr ? { participationInfo: participationInfoStr } : {}),
		...(onlineUrlStr ? { onlineUrl: onlineUrlStr } : {}),
		...(educationScheduleStr ? { educationSchedule: educationScheduleStr } : {}),
		...(openListOnlineAccessPeriodLabel ? { openListOnlineAccessPeriodLabel } : {}),
		...(hasDateRange && startDate
			? { courseStartDate: startDate, ...(endDate ? { courseEndDate: endDate } : {}) }
			: {}),
	};
}

/**
 * 현재 언어에 맞는 ClassItem 뷰 반환
 * — API `translations`에 현재 언어가 있으면 우선 사용하고, 공식 API 3어 외 UI 언어는 영어로 폴백합니다.
 */
export function getLocalizedClassItem(item: ClassItem, lang: string): ClassItem {
	const translations = item.translations;
	if (!translations) {
		return item;
	}
	const base = normalizeLocaleTag(lang);
	const localized =
		base === 'ko' || base === 'en' || base === 'ar'
			? translations[base]
			: translations.en;
	if (!localized) {
		return item;
	}
	const curriculum: CurriculumItem[] = (item.curriculum ?? []).map((c, i) => {
		const tr = localized.curriculum?.[i];
		return {
			...c,
			...(tr?.name != null && { name: tr.name }),
			...(tr?.includes != null && { includes: tr.includes }),
			...(tr?.practiceMode != null && { practiceMode: tr.practiceMode }),
		};
	});
	return {
		...item,
		...(localized.title != null && { title: localized.title }),
		...(localized.subtitle != null && { subtitle: localized.subtitle }),
		...(localized.description != null && { description: localized.description }),
		...(localized.organizationName != null && { organizationName: localized.organizationName }),
		...(localized.discountInfo != null && { discountInfo: localized.discountInfo }),
		...(localized.educationVenue != null && { educationVenue: localized.educationVenue }),
		...(localized.educationSchedule != null && { educationSchedule: localized.educationSchedule }),
		...(localized.onlinePlatform != null && { onlinePlatform: localized.onlinePlatform }),
		...(localized.recruitmentPeriod != null && {
			recruitmentPeriod: {
				startDate: localized.recruitmentPeriod.startDate ?? item.recruitmentPeriod?.startDate ?? '',
				endDate: localized.recruitmentPeriod.endDate ?? item.recruitmentPeriod?.endDate ?? '',
			},
		}),
		curriculum,
	};
}

/**
 * 오픈클래스 여부 (classType 우선, 없으면 판매/가격 휴리스틱)
 * — 마스터 사용자의 오픈클래스 수정·복제·세션 편집 제한 등에 사용
 */
export function isOpenClassItem(item: ClassItem | null | undefined): boolean {
	if (!item) return false;
	const ct = item.classType;
	if (ct === '오픈' || ct === 'open') return true;
	if (ct != null && ct !== '') return false;
	return item.isForSale === true || (item.price !== undefined && item.price > 0);
}
